import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { AsyncButton } from "../ui/async-button";
import { EmptyState } from "../ui/empty-state";
import { SectionCard } from "../ui/section-card";
import { Alert, AlertDescription } from "../ui/alert";
import { Field } from "../ui/field";
import { WorkbenchPage, WorkbenchPageContent } from "../ui/workbench-page";
import { PanelHeader } from "./PanelHeader";
import { userFacingError } from "../../../shared/userFacingError";
import {
  listEditorEngagements,
  type EditorEngagement,
} from "../../services/editorEngagements";
import { cloudAuthDesktopV2 } from "../../services/cloudAuthDesktopV2";
import { gatePlanFeature } from "../../../shared/planGate";

export function ClientsPanel({
  onClose,
  onOpenBookId,
  embedded = false,
}: {
  onClose?: () => void;
  onOpenBookId: (bookId: string) => Promise<void> | void;
  /** When true, omit the standalone header close control (used inside Settings). */
  embedded?: boolean;
}) {
  const [engagements, setEngagements] = useState<EditorEngagement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [rosterAllowed, setRosterAllowed] = useState<boolean | null>(null);
  const [rosterGateError, setRosterGateError] = useState<string | null>(null);
  const signedIn = cloudAuthDesktopV2.isSignedIn();

  const load = useCallback(async () => {
    setError(null);
    if (!cloudAuthDesktopV2.isSignedIn()) {
      setRosterAllowed(null);
      setRosterGateError(null);
      setEngagements([]);
      return;
    }
    try {
      const billing = await cloudAuthDesktopV2.getBillingPlan();
      const gate = gatePlanFeature(billing.plan, "editorRoster", false);
      if (!gate.ok) {
        setRosterAllowed(false);
        setRosterGateError(gate.error);
        setEngagements([]);
        return;
      }
      setRosterAllowed(true);
      setRosterGateError(null);
      const list = await listEditorEngagements();
      setEngagements(list);
    } catch {
      setError("Could not load client engagements.");
      setEngagements([]);
      setRosterAllowed(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function requestClient(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!signedIn) {
      setError("Sign in under Settings → Account first.");
      return;
    }
    if (rosterAllowed === false) {
      setError(rosterGateError || "Editor tools require an Editor plan.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cloudAuthDesktopV2.requestEditorialEngagement(email.trim(), message.trim());
      setEmail("");
      setMessage("");
      await load();
    } catch (err) {
      setError(userFacingError(err, "generic"));
    } finally {
      setBusy(false);
    }
  }

  async function openGrantedBook(bookId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onOpenBookId(bookId);
      onClose?.();
    } catch (err) {
      setError(userFacingError(err, "open"));
    } finally {
      setBusy(false);
    }
  }

  const approved = engagements.filter((e) => e.status === "approved");
  const pending = engagements.filter((e) => e.status === "pending");

  const content = (
    <div className="flex max-w-2xl flex-col gap-4">
      {!signedIn ? (
        <EmptyState
          compact
          title="Sign in to manage clients"
          description="Sign in under Settings → Account to request engagements and open granted books."
        />
      ) : rosterAllowed === false ? (
        <EmptyState
          compact
          title="Editor plan required"
          description={
            rosterGateError ||
            "Client roster and editorial engagements require an Editor plan. Upgrade under Settings → Billing."
          }
        />
      ) : (
        <>
          <form onSubmit={(e) => void requestClient(e)}>
            <SectionCard title="Request engagement" bodyClassName="flex flex-col gap-3">
              <Field label="Client email" htmlFor="clients-email">
                <Input
                  id="clients-email"
                  type="email"
                  required
                  placeholder="client@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Short note (optional)" htmlFor="clients-message">
                <Textarea
                  id="clients-message"
                  rows={2}
                  placeholder="Add context for the engagement request"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={busy}
                />
              </Field>
              <AsyncButton
                type="submit"
                size="sm"
                className="self-start"
                busy={busy}
                busyLabel="Working…"
                disabled={!email.trim()}
              >
                Send request
              </AsyncButton>
            </SectionCard>
          </form>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <SectionCard title={`Active (${approved.length})`} bodyClassName="space-y-0">
            {approved.length === 0 ? (
              <EmptyState compact title="No approved clients yet." />
            ) : (
              <ul className="flex list-none flex-col gap-2.5 p-0">
                {approved.map((eng) => (
                  <li key={eng.id} className="rounded-lg border border-border bg-card p-3">
                    <div className="flex items-center justify-between gap-2">
                      <strong className="min-w-0 text-sm text-foreground">
                        {(eng.client.name || "").trim() || eng.client.email}
                      </strong>
                      <span className="shrink-0 text-[0.6875rem] font-medium tracking-wide text-muted-foreground uppercase">
                        {eng.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{eng.client.email}</p>
                    {eng.grantedBooks.length === 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">No books granted yet.</p>
                    ) : (
                      <ul className="mt-2 flex list-none flex-col gap-1.5 p-0">
                        {eng.grantedBooks.map((g) => (
                          <li
                            key={`${eng.id}-${g.bookId}`}
                            className="flex items-center justify-between gap-3 text-[0.8125rem]"
                          >
                            <span className="min-w-0 wrap-break-word">{g.title || g.bookId}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => void openGrantedBook(g.bookId)}
                            >
                              Open
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard title={`Pending (${pending.length})`} bodyClassName="space-y-0">
            {pending.length === 0 ? (
              <EmptyState compact title="No pending requests." />
            ) : (
              <ul className="flex list-none flex-col gap-2.5 p-0">
                {pending.map((eng) => (
                  <li key={eng.id} className="rounded-lg border border-border bg-card p-3">
                    <div className="flex items-center justify-between gap-2">
                      <strong className="min-w-0 text-sm text-foreground">
                        {(eng.client.name || "").trim() || eng.client.email}
                      </strong>
                      <span className="shrink-0 text-[0.6875rem] font-medium tracking-wide text-muted-foreground uppercase">
                        {eng.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{eng.client.email}</p>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );

  const header = (
    <PanelHeader
      title="Clients"
      description="Editorial engagements and granted books. Opens into your Clients workspace folder when set."
      actions={
        !embedded && onClose ? (
          <Button type="button" size="sm" variant="outline" onClick={onClose}>
            Close
          </Button>
        ) : undefined
      }
      className={embedded ? "border-b-0 p-0" : undefined}
    />
  );

  if (embedded) {
    return (
      <div
        className="flex max-w-2xl flex-col gap-4 p-5"
        role="region"
        aria-label="Client engagements"
      >
        {header}
        {content}
      </div>
    );
  }

  return (
    <div className="h-full min-h-0" role="region" aria-label="Client engagements">
      <WorkbenchPage>
        {header}
        <WorkbenchPageContent>{content}</WorkbenchPageContent>
      </WorkbenchPage>
    </div>
  );
}
